/**
 * Variant Images / Media Manager gallery for the PDP.
 * Mixed into VariantSelects before the custom element is defined.
 */
(function (window) {
  // Persistent slides (e.g. the Vizbl 3D viewer) stay visible for every variant and are positioned by VariantSelects.
  const VARIANT_SLIDE = '.product-images__slide:not([data-persistent-slide])';
  const VARIANT_THUMB = '.product-thumbnail:not([data-persistent-slide])';

  const ProductMediaManagerMixin = {
    initMediaManager() {
      this.mediaManagerData = null;
      this.useMediaManager = false;
      if (!this.productSlider) return;

      const mediaManagerEl = document.getElementById(`MediaManagerData-${this.dataset.section}`);
      if (!mediaManagerEl) return;

      try {
        const parsed = JSON.parse(mediaManagerEl.textContent || '{}');
        if (parsed?.variants?.length) {
          this.mediaManagerData = parsed;
          this.useMediaManager = true;
        }
      } catch (e) {
        this.mediaManagerData = null;
      }
    },

    getMediaManagerMediaId(img) {
      return String(img?.shopifyMediaId || '').split('/').pop();
    },

    getMediaManagerNodeId(node) {
      const dataId = node?.dataset?.mediaId || node?.id || '';
      return dataId ? String(dataId.split('-').pop()) : null;
    },

    getShopifyCdnUrl(url, width) {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        if (width) parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (error) {
        return url;
      }
    },

    getMediaManagerSrcset(url) {
      return [375, 640, 960, 1280, 1600, 1920, 2048, 2560]
        .map((width) => `${this.getShopifyCdnUrl(url, width)} ${width}w`)
        .join(', ');
    },

    applyMediaManagerImageSources(imageEl, url, alt) {
      if (!imageEl || !url) return;
      const src = this.getShopifyCdnUrl(url, 960);
      const srcset = this.getMediaManagerSrcset(url);
      const thumbSrc = this.getShopifyCdnUrl(url, 160);
      imageEl.src = imageEl.closest('.product-thumbnail') ? thumbSrc : src;
      imageEl.srcset = imageEl.closest('.product-thumbnail') ? `${thumbSrc} 160w` : srcset;
      if (imageEl.hasAttribute('data-src')) imageEl.setAttribute('data-src', imageEl.src);
      if (imageEl.hasAttribute('data-srcset')) imageEl.setAttribute('data-srcset', imageEl.srcset);
      if (alt != null) imageEl.alt = alt;
      imageEl.addEventListener('load', () => {
        if (imageEl.naturalWidth) imageEl.width = imageEl.naturalWidth;
        if (imageEl.naturalHeight) imageEl.height = imageEl.naturalHeight;
      }, { once: true });
    },

    setMediaManagerNodeState(node, { active, initial }) {
      if (!node) return;
      node.classList.toggle('is-active', active);
      node.classList.toggle('is-initial-selected', Boolean(active && initial));
      node.classList.toggle('media-manager-hidden', !active);
    },

    bindMediaManagerZoom(slide) {
      const link = slide?.querySelector('.product-single__media-zoom');
      const slider = this.productSlider;
      if (!link || !slider || typeof slider.zoomClick !== 'function') return;
      if (link.dataset.mmZoomBound === 'true') return;
      link.dataset.mmZoomBound = 'true';
      link.addEventListener('click', (event) => slider.zoomClick(event, link));
    },

    syncMediaManagerFeaturedChrome(firstSlide) {
      if (!firstSlide || !this.productSlider) return;
      if (this.hasPersistentInitialSlide?.()) return;
      const slides = this.productSlider.querySelectorAll(VARIANT_SLIDE);
      if (!firstSlide.querySelector('.product-card--badges-pdp-wrapper')) {
        for (const slide of slides) {
          const badges = slide.querySelector('.product-card--badges-pdp-wrapper');
          if (!badges) continue;
          const media = firstSlide.querySelector('.product-single__media-image')
            || firstSlide.querySelector('.product-single__media');
          if (media) media.prepend(badges);
          break;
        }
      }
      const firstOverlay = firstSlide.querySelector('.product-media-overlay-actions');
      // An empty overlay container (e.g. left over in a cloned slide) must not block the move.
      if (!firstOverlay || !firstOverlay.children.length) {
        for (const slide of slides) {
          if (slide === firstSlide) continue;
          const opener = slide.querySelector('.product-media-overlay-actions');
          if (!opener || !opener.children.length) continue;
          firstOverlay?.remove();
          firstSlide.insertBefore(opener, firstSlide.firstChild);
          break;
        }
      }
    },

    ensureMediaManagerSlide(img, mediaId) {
      const existing = this.productSlider.querySelector(`[data-media-id="${this.dataset.section}-${mediaId}"]`);
      if (existing) return existing;
      const slide = this.createMediaManagerSlide(img, mediaId);
      if (!slide) return null;
      const parent = this.productSlider.querySelector('.flickity-slider') || this.productSlider;
      parent.appendChild(slide);
      this.bindMediaManagerZoom(slide);
      return slide;
    },

    ensureMediaManagerThumb(img, mediaId) {
      if (!this.thumbnails) return null;
      const existing = this.thumbnails.querySelector(`#Thumb-${this.dataset.section}-${mediaId}`);
      if (existing) return existing;
      const thumb = this.createMediaManagerThumb(img, mediaId);
      if (!thumb) return null;
      this.thumbnails.appendChild(thumb);
      return thumb;
    },

    createMediaManagerSlide(img, mediaId) {
      const isVideo = String(img?.mediaType || 'image').toLowerCase() === 'video';
      if (isVideo) return this.buildMediaManagerVideoSlide(img, mediaId);
      const template = this.productSlider.querySelector('.product-images__slide--image:not([data-media-manager-injected])')
        || this.productSlider.querySelector('.product-images__slide--image');
      if (template) return this.cloneMediaManagerImageSlide(template, img, mediaId);
      return this.buildMediaManagerImageSlide(img, mediaId);
    },

    createMediaManagerThumb(img, mediaId) {
      const template = this.thumbnails.querySelector(`${VARIANT_THUMB}:not([data-media-manager-injected])`)
        || this.thumbnails.querySelector(VARIANT_THUMB);
      if (template) return this.cloneMediaManagerThumb(template, img, mediaId);
      return this.buildMediaManagerThumb(img, mediaId);
    },

    cloneMediaManagerImageSlide(template, img, mediaId) {
      const slide = template.cloneNode(true);
      const sectionId = this.dataset.section;
      const alt = img?.alt || '';
      const url = img?.url || '';

      slide.id = `Slide-${sectionId}-${mediaId}`;
      slide.dataset.mediaId = `${sectionId}-${mediaId}`;
      slide.dataset.mediaManagerInjected = 'true';
      slide.classList.remove('is-selected', 'is-initial-selected', 'media-manager-hidden', 'product-images__slide-item--variant');
      slide.classList.add('is-active');

      const media = slide.querySelector('.product-single__media');
      if (media) media.id = `Media-Thumbnails-${sectionId}-${mediaId}`;

      slide.querySelector('.product-card--badges-pdp-wrapper')?.remove();
      // Remove the whole overlay container (not only the opener) so syncMediaManagerFeaturedChrome
      // can move the real "Sizing Guide" button into this slide when it becomes the first one.
      slide.querySelector('.product-media-overlay-actions')?.remove();
      slide.querySelector('.product-size-guide-opener')?.remove();
      slide.querySelector('.figcaption')?.remove();

      this.applyMediaManagerImageSources(slide.querySelector('img'), url, alt);

      const zoom = slide.querySelector('.product-single__media-zoom');
      if (zoom) {
        zoom.href = url;
        zoom.dataset.msrc = this.getShopifyCdnUrl(url, 20);
        delete zoom.dataset.mmZoomBound;
        const imageEl = slide.querySelector('img');
        if (imageEl) {
          imageEl.addEventListener('load', () => {
            zoom.dataset.w = String(imageEl.naturalWidth || '');
            zoom.dataset.h = String(imageEl.naturalHeight || '');
          }, { once: true });
        }
      }

      if (alt.includes('#')) {
        const imageSetFull = alt.split('#').pop();
        if (imageSetFull.includes('_')) {
          slide.dataset.setName = imageSetFull.split('_')[0];
          slide.dataset.group = imageSetFull;
        }
      } else {
        delete slide.dataset.setName;
        delete slide.dataset.group;
      }

      return slide;
    },

    buildMediaManagerImageSlide(img, mediaId) {
      const sectionId = this.dataset.section;
      const alt = img?.alt || '';
      const url = img?.url || '';
      const slide = document.createElement('div');
      slide.id = `Slide-${sectionId}-${mediaId}`;
      slide.className = 'product-images__slide product-images__slide--image is-active';
      slide.dataset.mediaId = `${sectionId}-${mediaId}`;
      slide.dataset.mediaManagerInjected = 'true';

      const media = document.createElement('div');
      media.className = 'product-single__media product-single__media-image aspect-ratio aspect-ratio--adapt';
      media.id = `Media-Thumbnails-${sectionId}-${mediaId}`;

      const zoomTemplate = this.productSlider.querySelector('.product-single__media-zoom');
      if (zoomTemplate && url) {
        const zoom = zoomTemplate.cloneNode(true);
        zoom.href = url;
        zoom.dataset.msrc = this.getShopifyCdnUrl(url, 20);
        media.appendChild(zoom);
      }

      const imageEl = document.createElement('img');
      imageEl.loading = 'lazy';
      imageEl.decoding = 'async';
      this.applyMediaManagerImageSources(imageEl, url, alt);
      media.appendChild(imageEl);
      slide.appendChild(media);
      return slide;
    },

    buildMediaManagerVideoSlide(img, mediaId) {
      const sourceUrl = img?.sourceUrl;
      if (!sourceUrl) return this.buildMediaManagerImageSlide(img, mediaId);

      const sectionId = this.dataset.section;
      const slide = document.createElement('div');
      slide.id = `Slide-${sectionId}-${mediaId}`;
      slide.className = 'product-images__slide product-images__slide--video is-active';
      slide.dataset.mediaId = `${sectionId}-${mediaId}`;
      slide.dataset.mediaManagerInjected = 'true';

      const media = document.createElement('div');
      media.className = 'product-single__media product-single__media-native-video';
      media.dataset.mediaId = mediaId;
      media.id = `Media-Thumbnails-${sectionId}-${mediaId}`;

      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (img?.url) video.poster = img.url;

      const source = document.createElement('source');
      source.src = sourceUrl;
      source.type = 'video/mp4';
      video.appendChild(source);
      media.appendChild(video);
      slide.appendChild(media);
      return slide;
    },

    cloneMediaManagerThumb(template, img, mediaId) {
      const thumb = template.cloneNode(true);
      const isVideo = String(img?.mediaType || 'image').toLowerCase() === 'video';
      thumb.id = `Thumb-${this.dataset.section}-${mediaId}`;
      thumb.dataset.mediaManagerInjected = 'true';
      thumb.classList.remove('is-initial-selected', 'media-manager-hidden', 'product-images__slide-item--variant');
      thumb.classList.add('is-active');
      delete thumb.dataset.setName;
      delete thumb.dataset.group;

      this.applyMediaManagerImageSources(thumb.querySelector('img'), img?.url, img?.alt || '');

      const existingIcon = thumb.querySelector('.product-thumbnail-icon');
      if (isVideo && !existingIcon) {
        const iconTemplate = this.thumbnails.querySelector('.product-thumbnail .product-thumbnail-icon');
        if (iconTemplate) thumb.insertBefore(iconTemplate.cloneNode(true), thumb.firstChild);
      } else if (!isVideo && existingIcon) {
        existingIcon.remove();
      }

      return thumb;
    },

    buildMediaManagerThumb(img, mediaId) {
      const thumb = document.createElement('div');
      thumb.id = `Thumb-${this.dataset.section}-${mediaId}`;
      thumb.className = 'product-thumbnail is-active';
      thumb.dataset.mediaManagerInjected = 'true';

      if (String(img?.mediaType || '').toLowerCase() === 'video') {
        const iconTemplate = this.thumbnails?.querySelector('.product-thumbnail-icon');
        if (iconTemplate) thumb.appendChild(iconTemplate.cloneNode(true));
      }

      const imageEl = document.createElement('img');
      imageEl.loading = 'lazy';
      this.applyMediaManagerImageSources(imageEl, img?.url, img?.alt || '');
      thumb.appendChild(imageEl);
      return thumb;
    },

    applyMediaManagerFilter() {
      if (!this.currentVariant || !this.mediaManagerData || !this.productSlider) return;

      const variantGidSuffix = String(this.currentVariant.id);
      const variantData = this.mediaManagerData.variants?.find(
        (v) => v.variantId && v.variantId.split('/').pop() === variantGidSuffix
      );
      const images = variantData?.images;

      if (variantData === undefined) {
        this._applyAltBasedFallback();
        return;
      }

      if (!images || images.length === 0) {
        this._applyAltBasedFallback();
        return;
      }

      const orderedIds = [];
      const orderedSlides = [];
      const orderedThumbs = [];

      images.forEach((img) => {
        const mediaId = this.getMediaManagerMediaId(img);
        if (!mediaId) return;

        const slide = this.ensureMediaManagerSlide(img, mediaId);
        if (!slide) return;

        orderedIds.push(mediaId);
        orderedSlides.push(slide);

        if (this.thumbnails) {
          const thumb = this.ensureMediaManagerThumb(img, mediaId);
          if (thumb) orderedThumbs.push(thumb);
        }
      });

      if (!orderedIds.length) {
        this._applyAltBasedFallback();
        return;
      }

      const idSet = new Set(orderedIds);
      const firstId = orderedIds[0];

      const slides = Array.from(this.productSlider.querySelectorAll(VARIANT_SLIDE));
      const thumbs = this.thumbnails
        ? Array.from(this.thumbnails.querySelectorAll(VARIANT_THUMB))
        : [];

      slides.forEach((slide) => {
        const id = this.getMediaManagerNodeId(slide);
        this.setMediaManagerNodeState(slide, {
          active: Boolean(id && idSet.has(id)),
          initial: id === firstId
        });
      });

      const slidesParent = orderedSlides[0]?.parentElement
        || this.productSlider.querySelector('.flickity-slider')
        || this.productSlider;
      const remainingSlides = slides.filter((slide) => {
        const id = this.getMediaManagerNodeId(slide);
        return !id || !idSet.has(id);
      });
      [...orderedSlides, ...remainingSlides].forEach((node) => {
        if (node && slidesParent) slidesParent.appendChild(node);
      });

      thumbs.forEach((thumb) => {
        const id = this.getMediaManagerNodeId(thumb);
        this.setMediaManagerNodeState(thumb, {
          active: Boolean(id && idSet.has(id)),
          initial: id === firstId
        });
      });

      if (thumbs.length || orderedThumbs.length) {
        const thumbsParent = this.thumbnails;
        const remainingThumbs = thumbs.filter((thumb) => {
          const id = this.getMediaManagerNodeId(thumb);
          return !id || !idSet.has(id);
        });
        [...orderedThumbs, ...remainingThumbs].forEach((node) => {
          if (node && thumbsParent) thumbsParent.appendChild(node);
        });
      }

      this.syncMediaManagerFeaturedChrome(orderedSlides[0]);

      const totalEl = this.productWrapper?.querySelector('.slider-counter--total');
      if (totalEl) totalEl.textContent = orderedIds.length;

      this.ensurePersistentMedia?.();

      if (this.productSlider.sliderItems) {
        this.productSlider.sliderItems = this.productSlider.querySelectorAll('[id^="Slide-"]');
      }
      if (typeof this.productSlider.onPaginationResize === 'function') {
        this.productSlider.onPaginationResize();
      }

      const mediaSelector = `#Slide-${this.dataset.section}-${firstId}`;
      const thumbSelector = `#Thumb-${this.dataset.section}-${firstId}`;

      if (this.thumbnails) {
        if (typeof this.productSlider.reInit === 'function') {
          this.productSlider.reInit(null);
        }
        const flkty = Flickity.data(this.productSlider);
        if (flkty && typeof this.productSlider.selectCell === 'function' && !this.hasPersistentInitialSlide?.()) {
          this.productSlider.selectCell(mediaSelector);
        }
        thumbs.forEach((thumb) => thumb.classList.remove('is-initial-selected'));
        orderedThumbs.forEach((thumb) => thumb.classList.remove('is-initial-selected'));
        const activeThumb = this.thumbnails.querySelector(thumbSelector);
        if (activeThumb) activeThumb.classList.add('is-initial-selected');
      } else {
        const activeSlide = this.productSlider.querySelector(mediaSelector);
        if (activeSlide && !this.hasPersistentInitialSlide?.()) {
          activeSlide.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' });
        }
      }
    },

    _applyAltBasedFallback() {
      if (this.productSlider) {
        this.productSlider.querySelectorAll('.product-images__slide').forEach((slide) => {
          slide.classList.add('is-active');
          slide.classList.remove('media-manager-hidden');
        });
      }
      if (this.thumbnails) {
        this.thumbnails.querySelectorAll('.product-thumbnail').forEach((thumb) => {
          thumb.classList.add('is-active');
          thumb.classList.remove('media-manager-hidden');
        });
      }

      this.setImageSet();
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;
      const mediaId = `#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      const activeMedia = this.productSlider.querySelector(mediaId);
      if (this.thumbnails) {
        this.setActiveMediaSlider(mediaId, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider);
      } else {
        this.setActiveMedia(activeMedia);
      }
    }
  };

  window.ProductMediaManagerMixin = ProductMediaManagerMixin;

  if (window.VariantSelects) {
    Object.assign(window.VariantSelects.prototype, ProductMediaManagerMixin);
  }
})(window);
